import { useState } from "react";
import { Trash2, PlusCircle } from "lucide-react";
import type { CandidateProfile } from "@applicant-reviewer/api";
import type { WorkHistoryItem } from "@applicant-reviewer/api";
import { useUpdateProfile } from "../../../../hooks/mutations/useProfileMutations";

interface WorkHistorySectionProps {
  profile: CandidateProfile;
}

type WorkHistoryEntry = WorkHistoryItem;

const CURRENT_YEAR = new Date().getFullYear();

function blankEntry(): WorkHistoryEntry {
  return {
    employer: "",
    role: "",
    startYear: CURRENT_YEAR,
    endYear: undefined,
    isCurrent: false,
  };
}

export default function WorkHistorySection({ profile }: WorkHistorySectionProps) {
  const [entries, setEntries] = useState<WorkHistoryEntry[]>(
    profile.workHistory && profile.workHistory.length > 0 ? profile.workHistory : [],
  );
  const [saved, setSaved] = useState(false);
  const updateProfile = useUpdateProfile();

  const updateEntry = (index: number, patch: Partial<WorkHistoryEntry>) => {
    setSaved(false);
    setEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry;
        const updated = { ...entry, ...patch };
        // Clear endYear when marking as current
        if (patch.isCurrent) {
          updated.endYear = undefined;
        }
        return updated;
      }),
    );
  };

  const addEntry = () => {
    setSaved(false);
    setEntries((prev) => [...prev, blankEntry()]);
  };

  const removeEntry = (index: number) => {
    setSaved(false);
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    setSaved(false);
    updateProfile.mutate(
      { workHistory: entries },
      { onSuccess: () => setSaved(true) },
    );
  };

  const inputClass =
    "w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0156FF] focus:border-transparent transition";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5">
      <h2 className="text-base font-bold text-gray-900">Work History</h2>

      {entries.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-4">
          No work history added yet.
        </p>
      )}

      {entries.map((entry, index) => (
        <div
          key={index}
          className="border border-gray-100 rounded-xl p-4 space-y-3 relative"
        >
          {/* Remove button */}
          <button
            type="button"
            onClick={() => removeEntry(index)}
            className="absolute top-3 right-3 p-1 text-gray-400 hover:text-red-500 transition-colors"
            aria-label="Remove entry"
          >
            <Trash2 className="w-4 h-4" />
          </button>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Employer</label>
            <input
              type="text"
              value={entry.employer}
              onChange={(e) => updateEntry(index, { employer: e.target.value })}
              placeholder="Company name"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Role / Title</label>
            <input
              type="text"
              value={entry.role}
              onChange={(e) => updateEntry(index, { role: e.target.value })}
              placeholder="e.g. Cashier"
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Start Year</label>
              <input
                type="number"
                value={entry.startYear}
                onChange={(e) => updateEntry(index, { startYear: Number(e.target.value) })}
                min={1950}
                max={CURRENT_YEAR}
                className={inputClass}
              />
            </div>

            {!entry.isCurrent && (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">End Year</label>
                <input
                  type="number"
                  value={entry.endYear ?? ""}
                  onChange={(e) =>
                    updateEntry(index, {
                      endYear: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  min={entry.startYear}
                  max={CURRENT_YEAR}
                  placeholder={String(CURRENT_YEAR)}
                  className={inputClass}
                />
              </div>
            )}
          </div>

          {/* isCurrent checkbox */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={entry.isCurrent}
              onChange={(e) => updateEntry(index, { isCurrent: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-[#0156FF] focus:ring-[#0156FF]"
            />
            <span className="text-xs font-semibold text-gray-700">I currently work here</span>
          </label>

          {/* Display summary */}
          <p className="text-xs text-gray-400">
            {entry.employer || "Employer"} — {entry.role || "Role"} (
            {entry.startYear}–{entry.isCurrent ? "Present" : (entry.endYear ?? "...")})
          </p>
        </div>
      ))}

      {/* Add button */}
      <button
        type="button"
        onClick={addEntry}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-gray-200 text-sm font-semibold text-gray-500 hover:border-[#0156FF] hover:text-[#0156FF] transition-colors"
      >
        <PlusCircle className="w-4 h-4" />
        Add Experience
      </button>

      {updateProfile.isError && (
        <p className="text-xs text-red-600">Save failed. Please try again.</p>
      )}

      {saved && !updateProfile.isPending && (
        <p className="text-xs text-green-600 font-semibold">Saved!</p>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={updateProfile.isPending}
        className="w-full h-12 rounded-full bg-[#0156FF] text-white text-sm font-bold hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {updateProfile.isPending ? "Saving..." : "Save Work History"}
      </button>
    </div>
  );
}
